import React from 'react';
import { alpha, Box, Stack, Theme, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import { MoveBlockState } from './EditPanelBridge';

function gatesKey(block: MoveBlockState): string {
    return block.inconsistencies && block.clarifications
        ? 'both'
        : block.inconsistencies
          ? 'inconsistencies'
          : 'clarifications';
}

function missingKey(block: MoveBlockState): string {
    const privilege = block.missingAccess.includes('privilege');
    const reportEdit = block.missingAccess.includes('report_edit');
    return privilege && reportEdit ? 'missing_both' : privilege ? 'missing_privilege' : 'missing_report_edit';
}

// §3.1 move-blocked banner: no dismiss button — the host hides it on tab change.
// Re-mount with a new key (attempt) to re-run the shake animation.
export default function MoveBlockedBanner({ block }: { block: MoveBlockState }) {
    const { t } = useBundleTranslation('app/content-workflow/element-editor');
    const canResolve = block.missingAccess.length === 0;

    return (
        <Stack
            data-test="move_blocked_banner"
            role="alert"
            aria-live="assertive"
            direction="row"
            spacing={1.5}
            sx={{
                p: 1.5,
                borderRadius: '3px',
                borderLeft: '3px solid',
                borderColor: 'error.main',
                bgcolor: (theme: Theme) => alpha(theme.palette.error.main, 0.08),
                '@keyframes miMoveBlockedShake': {
                    '10%, 90%': { transform: 'translateX(-1px)' },
                    '20%, 80%': { transform: 'translateX(2px)' },
                    '30%, 50%, 70%': { transform: 'translateX(-3px)' },
                    '40%, 60%': { transform: 'translateX(3px)' },
                },
                animation: 'miMoveBlockedShake 0.5s ease',
            }}
        >
            <IconMi icon="alert" sx={{ color: 'error.main', fontSize: '16px', mt: '2px', flexShrink: 0 }} />
            <Box>
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 600, color: 'error.main' }}>
                    {t('move_blocked_title')}
                </Typography>{' '}
                <Typography component="span" sx={{ fontSize: '13px', color: 'text.primary' }}>
                    {canResolve
                        ? t(`move_blocked_${gatesKey(block)}`)
                        : t(`move_blocked_no_access_${gatesKey(block)}`, { missing: t(missingKey(block)) })}
                    {!canResolve && ` ${t('move_blocked_ask_admin')}`}
                </Typography>
            </Box>
        </Stack>
    );
}
